import {
  AppstoreOutlined,
  BellOutlined,
  BookOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
  ReadOutlined,
  ShoppingCartOutlined,
  UserOutlined
} from "@ant-design/icons";
import { Dropdown, Image, message, Popover } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CartMini from "./cardMini";
import { useEffect, useState, useRef, useLayoutEffect } from "react";
import * as CONSTANTS from "../utils/constants";
import { getCookie } from "../utils/localStorageHandle";
import { useDevice } from "../hooks";
import logo from "../assets/demo/logo.svg";
import logoMobile from "../assets/demo/logo.svg";
import AutoCompleteCustom from "./autoCompleteCustom";
import NotificationList from "./notificationList";
import { scrollToPageTop } from "./scrollTop";

export default function Header() {
  const { isMobile } = useDevice();
  const location = useLocation();
  const navigate = useNavigate();

  const [cart, setCart] = useState([]);
  const [avatarError, setAvatarError] = useState(false);

  const mobileHeaderRef = useRef(null);
  const [mobileHeaderHeight, setMobileHeaderHeight] = useState(0);

  useLayoutEffect(() => {
    if (!isMobile) {
      setMobileHeaderHeight(0);
      return;
    }

    const header = mobileHeaderRef.current;
    if (!header) return;

    const updateHeaderHeight = () => {
      const rect = header.getBoundingClientRect();
      setMobileHeaderHeight(Math.ceil(rect.height));
    };

    updateHeaderHeight();

    let observer;

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateHeaderHeight);
      observer.observe(header);
    }

    window.addEventListener("resize", updateHeaderHeight);
    window.addEventListener("orientationchange", updateHeaderHeight);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", updateHeaderHeight);
      window.removeEventListener("orientationchange", updateHeaderHeight);
    };
  }, [isMobile]);

  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    user = null;
  }

  useEffect(() => {
    scrollToPageTop();
  }, [location.pathname]);

  useEffect(() => {
    const readCart = () => {
      try {
        const value = JSON.parse(getCookie(CONSTANTS.cartCookie));
        setCart(Array.isArray(value) ? value : []);
      } catch (e) {
        setCart([]);
      }
    };

    readCart();
    window.addEventListener("focus", readCart);

    return () => window.removeEventListener("focus", readCart);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    message.success("Đăng xuất thành công!");
    navigate("/dang-nhap");
  };

  const accountItems = [
    {
      key: "medical-ai",
      label: (
        <a href="/medical-ai.html">
          Y Khoa AI
        </a>
      )
    },
    {
      key: "profile",
      label: <Link to="/thong-tin-ca-nhan">Thông tin cá nhân</Link>
    },
    {
      key: "logout",
      label: (
        <button
          type="button"
          onClick={handleLogout}
          className="border-0 bg-transparent p-0 cursor-pointer"
        >
          Thoát
        </button>
      )
    }
  ];

  const navItems = [
    {
      label: "Danh mục",
      icon: <AppstoreOutlined />,
      path: "/danh-muc-san-pham/Sach-and-Tai-Lieu"
    },
    {
      label: "Sách",
      icon: <BookOutlined />,
      path: "/danh-muc-san-pham/Sach-and-Tai-Lieu"
    },
    {
      label: "Thiết bị",
      icon: <MedicineBoxOutlined />,
      path: "/danh-muc-san-pham/Thiet-Bi-Y-Te"
    },
    {
      label: "Tài liệu",
      icon: <FileTextOutlined />,
      path: "/danh-muc-san-pham/Sach-and-Tai-Lieu"
    },
    {
      label: "Blog",
      icon: <ReadOutlined />,
      path: "/blog"
    },
    {
      label: "Tiện ích",
      icon: <AppstoreOutlined />,
      path: "/tienich"
    }
  ];

  const hideStandardHeader =
    location?.pathname?.includes("/san-pham") ||
    location?.pathname?.includes("/sach-cua-toi/");

  if (isMobile) {
    if (hideStandardHeader) return null;

    return (
      <>
      <header
        ref={mobileHeaderRef}
        className={`liquid-glass-header cyk-mobile-header ${
          location?.pathname === "/"
            ? "cyk-mobile-header-home"
            : "cyk-mobile-header-subpage"
        }`}
      >
        <div className="cyk-mobile-header-inner">
          <div className="cyk-mobile-one-row">
            <Link
              to="/"
              className="cyk-mobile-logo"
              aria-label="Trang chủ"
            >
              <img
                src={logoMobile}
                alt="Cửa Hàng Y Khoa"
              />
            </Link>

            <div className="cyk-mobile-search">
              <AutoCompleteCustom />
            </div>

            <div className="cyk-mobile-actions">
              {user && (
                <Popover
                  content={<NotificationList />}
                  trigger="click"
                  placement="bottomRight"
                  overlayClassName="notification-popover-mobile"
                  arrow={false}
                >
                  <button
                    type="button"
                    className="cyk-header-action"
                    aria-label="Thông báo"
                  >
                    <BellOutlined />
                  </button>
                </Popover>
              )}

              <button
                type="button"
                className="cyk-header-action"
                aria-label="Giỏ hàng"
                onClick={() => navigate("/gio-hang")}
              >
                <ShoppingCartOutlined />

                {cart?.length > 0 && (
                  <span className="cyk-header-badge">
                    {cart.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>
      {location?.pathname === "/" && (
        <div
          className="cyk-mobile-header-spacer"
          aria-hidden="true"
          style={{
            height: mobileHeaderHeight
              ? `${mobileHeaderHeight}px`
              : undefined
          }}
        />
      )}
      </>
    );
  }

  return (
    <header className="liquid-glass-header cyk-desktop-header">
      <nav className="liquid-glass-desktop cyk-desktop-nav">
        <div className="cyk-desktop-main">
          <Link to="/" className="cyk-desktop-logo" aria-label="Trang chủ">
            <img
              src={logo}
              alt="Cửa Hàng Y Khoa"
              className="cyk-desktop-logo-image"
            />
          </Link>

          <div className="cyk-desktop-search">
            <AutoCompleteCustom />
          </div>

          <div className="cyk-desktop-actions">
            <Popover
              placement="bottomRight"
              trigger="click"
              content={<NotificationList />}
            >
              <button
                type="button"
                className="cyk-desktop-icon-button"
                aria-label="Thông báo"
                title="Thông báo"
              >
                <BellOutlined />
              </button>
            </Popover>

            <Popover
              placement="bottomRight"
              trigger="click"
              content={<CartMini />}
            >
              <button
                type="button"
                className="cyk-desktop-icon-button"
                aria-label="Giỏ hàng"
                title="Giỏ hàng"
              >
                <ShoppingCartOutlined />

                {cart.length > 0 && (
                  <span className="cyk-desktop-icon-badge">
                    {cart.length > 99 ? "99+" : cart.length}
                  </span>
                )}
              </button>
            </Popover>

            {user ? (
              <Dropdown
                menu={{ items: accountItems }}
                placement="bottomRight"
                trigger={["click"]}
                overlayClassName="cyk-account-dropdown"
              >
                <button
                  type="button"
                  className="cyk-desktop-icon-button cyk-desktop-account-button"
                  aria-label="Tài khoản"
                  title="Tài khoản"
                >
                  {user?.photos && !avatarError ? (
                    <img
                      src={user.photos}
                      alt="Tài khoản"
                      className="cyk-desktop-avatar"
                      onError={() => setAvatarError(true)}
                    />
                  ) : (
                    <UserOutlined />
                  )}
                </button>
              </Dropdown>
            ) : (
              <button
                type="button"
                className="cyk-desktop-icon-button cyk-desktop-account-button"
                aria-label="Đăng nhập"
                title="Đăng nhập"
                onClick={() => navigate("/dang-nhap")}
              >
                <UserOutlined />
              </button>
            )}
          </div>
        </div>

        
      </nav>
    </header>
  );
}
